import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { apiFetch, apiUrl } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { logger } from '@/lib/logger';
import {
  SearchScope,
  SearchResult,
  ContactResult,
  DealResult,
  NoteResult,
  fuzzyMatch,
  scoreResult,
} from '@/lib/search-utils';
import type {
  BoardResponse,
  ContactsListResponse,
  ContactsListPaginatedResponse,
  Contact,
  CrmDealsSearchResponse,
  CrmNotesSearchResponse,
  Deal,
  Stage,
} from '@shared/api';

export function useGlobalSearch(query: string, scope: SearchScope = 'all') {

  const board = useQuery({
    queryKey: queryKeys.crmBoard,
    queryFn: async ({ signal }) => {
      logger.debug('Fetching board from:', apiUrl('/api/crm/board'));
      const r = await apiFetch('/api/crm/board', { credentials: 'include', signal });
      if (!r.ok) {
        logger.error(`Board fetch failed with status ${r.status}`);
        throw new Error(`Board fetch failed with status ${r.status}`);
      }
      const data = await r.json() as BoardResponse;
      logger.debug('Board fetched successfully');
      return data;
    },
    retry: (failureCount, error) => {
      // Retry up to 3 times with exponential backoff
      if (failureCount < 3) {
        return true;
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff: 1s, 2s, 4s, max 30s
    staleTime: 5 * 60 * 1000, // 5 minutes
    // Don't cache errors - let React Query handle retries
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    // Return empty board on final failure to keep app functional
    placeholderData: {
      stages: [],
      deals: [],
      contacts: [],
      pipelines: [],
    } as BoardResponse,
  });

  const contacts = useQuery({
    queryKey: queryKeys.crmContacts,
    queryFn: async ({ signal }) => {
      try {
        const url = apiUrl('/api/crm/contacts');
        logger.debug('Fetching contacts from:', url);
        const r = await apiFetch('/api/crm/contacts', { credentials: 'include', signal });
        if (!r.ok) throw new Error(`Contacts fetch failed with status ${r.status}`);
        const data = await r.json() as ContactsListResponse;
        logger.debug('Contacts fetched:', data.contacts?.length || 0);
        return data.contacts;
      } catch (err) {
        logger.error('Contacts fetch error:', err instanceof Error ? err.message : String(err));
        // Return empty array instead of throwing to keep app functional
        return [] as Contact[];
      }
    },
    retry: 1,
  });

  const trimmedQuery = query.trim();
  const shouldFetchNotes = trimmedQuery.length >= 2 && (scope === 'all' || scope === 'notes');
  const shouldFetchDealSearch =
    trimmedQuery.length >= 2 && (scope === 'all' || scope === 'deals');
  const shouldFetchContactSearch =
    trimmedQuery.length >= 2 && (scope === 'all' || scope === 'contacts');

  const dealsSearch = useQuery({
    queryKey: ['crm-deals-search', trimmedQuery],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('q', trimmedQuery);
      params.set('limit', '50');
      const r = await apiFetch(`/api/crm/deals/search?${params.toString()}`, {
        credentials: 'include',
        signal,
      });
      if (!r.ok) {
        throw new Error(`Deal search failed with status ${r.status}`);
      }
      const data = (await r.json()) as CrmDealsSearchResponse;
      return Array.isArray(data.deals) ? data.deals : [];
    },
    enabled: shouldFetchDealSearch,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const contactsSearch = useQuery({
    queryKey: ['crm-contacts-search', trimmedQuery],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('search', trimmedQuery);
      params.set('page', '1');
      params.set('pageSize', '50');
      const r = await apiFetch(`/api/crm/contacts?${params.toString()}`, {
        credentials: 'include',
        signal,
      });
      if (!r.ok) {
        throw new Error(`Contact search failed with status ${r.status}`);
      }
      const data = (await r.json()) as ContactsListPaginatedResponse;
      return Array.isArray(data.contacts) ? data.contacts : [];
    },
    enabled: shouldFetchContactSearch,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  const notesSearch = useQuery({
    queryKey: ['crm-notes-search', trimmedQuery],
    queryFn: async ({ signal }) => {
      const params = new URLSearchParams();
      params.set('q', trimmedQuery);
      params.set('limit', '100');
      const r = await apiFetch(`/api/crm/notes/search?${params.toString()}`, {
        credentials: 'include',
        signal,
      });
      if (!r.ok) {
        throw new Error(`Notes search failed with status ${r.status}`);
      }
      const data = (await r.json()) as CrmNotesSearchResponse;
      return Array.isArray(data.notes) ? data.notes : [];
    },
    enabled: shouldFetchNotes,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
  });

  // Perform search filtering and sorting
  const results = useMemo(() => {
    const trimmedQuery = query.trim().toLowerCase();

    if (!trimmedQuery) {
      return [];
    }

    const searchResults: SearchResult[] = [];

    try {
      // Search Deals (board = active stages; API = includes Deal Lost)
      if (scope === 'all' || scope === 'deals') {
        const deals = board.data?.deals || [];
        const contactsData = contacts.data || [];
        const stagesData = board.data?.stages || [];
        const dealIdsAdded = new Set<string>();

        const pushDealResult = (deal: Deal, relatedContact: Contact | undefined, relatedStage: Stage | undefined) => {
          if (dealIdsAdded.has(deal.id)) return;
          dealIdsAdded.add(deal.id);
          searchResults.push({
            type: 'deal',
            id: deal.id,
            title: deal.title,
            contactId: deal.contactId,
            contactName: relatedContact?.name,
            contactEmail: relatedContact?.email,
            stageId: deal.stageId,
            stageName: relatedStage?.name,
            closingDate: deal.closingDate || '',
            width: deal.width?.toString(),
            height: deal.height?.toString(),
            color: deal.color || '',
          } as DealResult);
        };

        deals.forEach((deal: Deal) => {
          try {
            const relatedContact = contactsData.find(
              (c: Contact) => c.id === deal.contactId
            );
            const relatedStage = stagesData.find(
              (s: Stage) => s.id === deal.stageId
            );

            const fieldsToSearch = [
              deal.title || '',
              deal.width?.toString() || '',
              deal.height?.toString() || '',
              deal.color || '',
              relatedContact?.name || '',
              relatedContact?.email || '',
            ];

            const isMatch = fieldsToSearch.some((field) =>
              fuzzyMatch(field, trimmedQuery)
            );

            if (isMatch) {
              pushDealResult(deal, relatedContact, relatedStage);
            }
          } catch (err) {
            logger.warn('Error processing deal:', deal.id, err);
          }
        });

        const extraDeals = shouldFetchDealSearch ? dealsSearch.data ?? [] : [];
        extraDeals.forEach((deal: Deal & { contact?: Contact | null }) => {
          try {
            const embedded = deal.contact ?? undefined;
            const relatedContact =
              embedded ??
              contactsData.find((c: Contact) => c.id === deal.contactId);
            const stageId = deal.stageId ?? deal.stage_id;
            const relatedStage = stagesData.find((s: Stage) => s.id === stageId);
            pushDealResult(
              { ...deal, stageId: stageId ?? deal.stageId } as Deal,
              relatedContact,
              relatedStage
            );
          } catch (err) {
            logger.warn('Error processing search API deal:', deal.id, err);
          }
        });
      }

      // Search Contacts (full list + server search so older contacts are never missing)
      if (scope === 'all' || scope === 'contacts') {
        const contactsData = contacts.data || [];
        const contactIdsAdded = new Set<string>();

        const pushContactResult = (contact: Contact) => {
          if (!contact?.id || contactIdsAdded.has(contact.id)) return;
          contactIdsAdded.add(contact.id);
          searchResults.push({
            type: 'contact',
            id: contact.id,
            name: contact.name,
            email: contact.email,
            phone: contact.phone,
            city: contact.city,
            isCompany: contact.isCompany,
            ico: contact.ico,
          } as ContactResult);
        };

        contactsData.forEach((contact: Contact) => {
          try {
            const fieldsToSearch = [
              contact.name || '',
              contact.email || '',
            ];

            const isMatch = fieldsToSearch.some((field) =>
              fuzzyMatch(field, trimmedQuery)
            );

            if (isMatch) {
              pushContactResult(contact);
            }
          } catch (err) {
            logger.warn('Error processing contact:', contact.id, err);
          }
        });

        const extraContacts = shouldFetchContactSearch ? contactsSearch.data ?? [] : [];
        extraContacts.forEach((contact: Contact) => {
          try {
            pushContactResult(contact);
          } catch (err) {
            logger.warn('Error processing search API contact:', contact.id, err);
          }
        });
      }

      // Search Notes (server scans all deal notes, incl. lost deals)
      if (scope === 'all' || scope === 'notes') {
        const notes = shouldFetchNotes ? notesSearch.data ?? [] : [];
        const contactsData = contacts.data || [];

        notes.forEach((note: any) => {
          try {
            const embedded = note.contact as Contact | undefined;
            const relatedContact = embedded
              ? embedded
              : note.contactId
                ? contactsData.find((c: any) => c.id === note.contactId)
                : null;

            const fieldsToSearch = [
              note.body || '',
              note.dealTitle || '',
              relatedContact?.name || '',
              relatedContact?.email || '',
            ];

            const isMatch = fieldsToSearch.some((field) =>
              fuzzyMatch(field, trimmedQuery)
            );

            if (isMatch) {
              searchResults.push({
                type: 'note',
                id: note.id,
                body: note.body,
                dealId: note.dealId,
                dealTitle: note.dealTitle,
                contactId: relatedContact?.id,
                contactName: relatedContact?.name,
                contactEmail: relatedContact?.email,
                createdAt: note.createdAt,
              } as NoteResult);
            }
          } catch (err) {
            logger.warn('Error processing note:', note.id, err);
          }
        });
      }

      // Sort by type order and score
      const typeOrder = { deal: 0, contact: 1, note: 2 };
      searchResults.sort((a, b) => {
        const typeCompare =
          typeOrder[a.type as keyof typeof typeOrder] -
          typeOrder[b.type as keyof typeof typeOrder];
        if (typeCompare !== 0) return typeCompare;

        const scoreA = scoreResult(a, trimmedQuery);
        const scoreB = scoreResult(b, trimmedQuery);

        if (scoreA !== scoreB) return scoreB - scoreA;

        return 0;
      });
    } catch (err) {
      logger.error('Error during search:', err);
    }

    return searchResults;
  }, [
    query,
    scope,
    board.data,
    contacts.data,
    contactsSearch.data,
    notesSearch.data,
    dealsSearch.data,
    shouldFetchDealSearch,
    shouldFetchContactSearch,
    shouldFetchNotes,
  ]);

  const dealSearchLoading = shouldFetchDealSearch && dealsSearch.isLoading;
  const contactSearchLoading = shouldFetchContactSearch && contactsSearch.isLoading;
  const notesSearchLoading = shouldFetchNotes && notesSearch.isLoading;

  return {
    results,
    isLoading:
      board.isLoading ||
      contacts.isLoading ||
      notesSearchLoading ||
      dealSearchLoading ||
      contactSearchLoading,
    isError:
      board.isError ||
      contacts.isError ||
      notesSearch.isError ||
      dealsSearch.isError ||
      contactsSearch.isError,
    error:
      board.error ||
      contacts.error ||
      notesSearch.error ||
      dealsSearch.error ||
      contactsSearch.error,
  };
}
